import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WebhookRow {
  id: string;
  api_key_id: string;
  target_url: string;
  event_types: string[];
  enabled: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateWebhookInput {
  api_key_id: string;
  target_url: string;
  event_types: string[];
  description?: string;
}

export interface CreateWebhookResult extends WebhookRow {
  secret: string; // returned only once on creation
}

export interface WebhookDeliveryRow {
  id: string;
  webhook_id: string | null;
  event: string;
  event_type: string | null;
  delivered: boolean;
  attempts: number;
  status_code: number | null;
  succeeded_at: string | null;
  created_at: string;
  last_attempted_at: string | null;
  webhooks: { target_url: string } | null;
}

export const WEBHOOK_EVENTS = [
  'invoice.created',
  'invoice.updated',
  'payment.received',
  'ticket.opened',
  'ticket.updated',
  'attendance.checked_in',
  'attendance.checked_out',
  'quote.created',
  'quote.accepted',
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENTS)[number];

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const webhookKeys = {
  all: ['webhooks'] as const,
  list: (tenantId: string) => [...webhookKeys.all, 'list', tenantId] as const,
  deliveries: (tenantId: string) => [...webhookKeys.all, 'deliveries', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const supabaseUrl = (supabase as unknown as { supabaseUrl: string }).supabaseUrl ?? '';
  const base = supabaseUrl.replace('/rest/v1', '');
  const res = await fetch(`${base}/functions/v1/api-keys${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useWebhooks(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: webhookKeys.list(tenantId),
    queryFn: async () => {
      const result = await apiFetch<{ data: WebhookRow[] }>(supabase, '/webhooks');
      return result.data;
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateWebhook(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWebhookInput) =>
      apiFetch<{ data: CreateWebhookResult }>(supabase, '/webhooks', {
        method: 'POST',
        body: JSON.stringify(input),
      }).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: webhookKeys.list(tenantId) });
    },
  });
}

export function useToggleWebhook(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiFetch<undefined>(supabase, `/webhooks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: webhookKeys.list(tenantId) });
    },
  });
}

export function useDeleteWebhook(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (webhookId: string) =>
      apiFetch<undefined>(supabase, `/webhooks/${webhookId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: webhookKeys.list(tenantId) });
    },
  });
}

export interface TestResult {
  ok: boolean;
  status: number;
  response?: string;
  error?: string;
}

export function useTestWebhook(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (webhookId: string) =>
      apiFetch<TestResult>(supabase, `/webhooks/${webhookId}/test`, { method: 'POST' }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: webhookKeys.deliveries(tenantId) });
    },
  });
}

export function useRetryDelivery(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deliveryId: string) =>
      apiFetch<{ ok: boolean; status: number }>(
        supabase,
        `/webhook-deliveries/${deliveryId}/retry`,
        { method: 'POST' },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: webhookKeys.deliveries(tenantId) });
    },
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SupabaseWithFrom = SupabaseClientLike & { from: (...args: any[]) => any };

export function useWebhookDeliveries(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: webhookKeys.deliveries(tenantId),
    queryFn: async () => {
      const db = supabase as SupabaseWithFrom;
      const { data, error } = (await db
        .from('webhook_deliveries')
        .select(
          'id, webhook_id, event, event_type, delivered, attempts, status_code, succeeded_at, created_at, last_attempted_at, webhooks(target_url)',
        )
        .order('created_at', { ascending: false })
        .limit(200)) as { data: WebhookDeliveryRow[] | null; error: { message: string } | null };
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    enabled: Boolean(tenantId),
  });
}
